import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Periodic from './Periodic';

const Footnote = styled.div`
	font-size: .7rem;
	line-height: 1rem;
	margin-bottom: 10px;
`;

const HomeWrapper = styled.div`

	article {
		color: ${({ theme }) => theme.primaryColor};
		font-size: 1.0rem;
		line-height: 2;
		margin-bottom: 15px;
	}

	article .article-title {
		cursor: pointer;
		font-size: 1rem;
		color: ${({ theme }) => theme.headingColor};
	}
	article .article-date {
		display: none;
		float: right;
	}
`;

const ToggleRead = styled.div`
	&.article-closed {
		display: none;
	}
	&.article-opened {
		display: block;
	}
`;

const articleSlug = title => String(title)
	.toLowerCase()
	.normalize('NFKD')
	.replace(/[\u0300-\u036f]/g, '')
	.replace(/[^a-z0-9]+/g, '-')
	.replace(/(^-|-$)/g, '');

const writeToClipboard = async text => {
	try {
		await navigator.clipboard.writeText(text);
	} catch {
		const textarea = document.createElement('textarea');
		textarea.value = text;
		Object.assign(textarea.style, { position: 'fixed', left: '-10000px' });
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand('copy');
		textarea.remove();
	}
};

const ArticleTitle = ({ children }) => {
	const [copied, setCopied] = useState(false);
	const [linkCopied, setLinkCopied] = useState(false);
	const feedbackTimer = React.useRef(null);

	useEffect(() => () => window.clearTimeout(feedbackTimer.current), []);

	const copyArticle = async event => {
		event.stopPropagation();
		const article = event.currentTarget.closest('article');
		const content = article?.querySelector('.article-closed, .article-opened');
		if (!article || !content) return;

		const copy = content.cloneNode(true);
		copy.querySelectorAll('button, .sr-only').forEach(element => element.remove());
		copy.classList.remove('article-closed', 'article-opened');
		Object.assign(copy.style, { position: 'fixed', left: '-10000px', top: '0', display: 'block', width: '700px' });
		document.body.appendChild(copy);
		const text = `${String(children)}\n\n${copy.innerText.trim()}`;
		copy.remove();

		await writeToClipboard(text);

		setCopied(true);
		setLinkCopied(false);
		window.clearTimeout(feedbackTimer.current);
		feedbackTimer.current = window.setTimeout(() => setCopied(false), 1600);
	};

	const copyArticleLink = async event => {
		event.stopPropagation();
		const article = event.currentTarget.closest('article');
		if (!article) return;

		if (!article.id) article.id = articleSlug(children);
		const url = new URL(window.location.href);
		url.hash = article.id;
		await writeToClipboard(url.toString());

		setLinkCopied(true);
		setCopied(false);
		window.clearTimeout(feedbackTimer.current);
		feedbackTimer.current = window.setTimeout(() => setLinkCopied(false), 1600);
	};

	return (
		<div className="article-title">
			<span className="article-title-text">{children}</span>
			<button type="button" className="article-link" onClick={copyArticleLink} aria-label={`Copy link to ${children}`}>
				{linkCopied ? <span className="article-action-feedback">Link copied</span> : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1"/><path d="M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1"/></svg>}
			</button>
			<button type="button" className="article-copy" onClick={copyArticle} aria-label={`Copy ${children}`}>
				{copied ? <span className="article-action-feedback">Copied</span> : <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="8" width="11" height="11" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/></svg>}
			</button>
		</div>
	);
};


// Click an article title to toggle it open or closed.
const readArticle = e => {
	e.preventDefault();

	var classList = e.currentTarget.nextElementSibling.classList;
	if (classList.contains('article-closed')) {
		classList.remove('article-closed');
		classList.add('article-opened');
	} else {
		classList.remove('article-opened');
		classList.add('article-closed');
	}
}

const MissingCenterSquare = () => (
  <div className="missing-square" aria-label="A five by five square of dots with its center dot missing">
    {Array.from({ length: 25 }, (_, i) => {
      const row = Math.floor(i / 5);
      const column = i % 5;
      let region = 'center';
      if (row < 2 && column < 3) region = 'top';
      else if (column > 2 && row < 3) region = 'right';
      else if (row > 2 && column > 1) region = 'bottom';
      else if (column < 2 && row > 1) region = 'left';
      return <span key={i} className={`square-dot ${region}`}></span>;
    })}
  </div>
);

const ConsecutiveProductAnimation = () => {
	const [firstNumber, setFirstNumber] = useState(2);
	const [animationKey, setAnimationKey] = useState(0);
	const blockWidth = firstNumber + 1;
	const rectangleRows = firstNumber * (firstNumber + 2);
	const rectangleColumns = (firstNumber + 1) * (firstNumber + 3);
	const squareSide = firstNumber * firstNumber + 3 * firstNumber + 1;
	const product = rectangleRows * rectangleColumns;
	const center = Math.floor(squareSide / 2);
	const movingDotCount = product - rectangleRows * squareSide;
	const moveStart = 3.15;
	const moveStep = Math.min(.035, 1.5 / movingDotCount);
	const centerMoveDelay = moveStart + movingDotCount * moveStep + .75;
	const squareCenterDelay = centerMoveDelay + 1;
	const squareShift = ((rectangleColumns - squareSide) / (2 * rectangleColumns)) * 100;

	const dots = Array.from({ length: product }, (_, index) => {
		const row = Math.floor(index / rectangleColumns);
		const column = index % rectangleColumns;
		let group = 4;
		let groupIndex = row * (rectangleColumns - blockWidth) + column - blockWidth;

		if (row < firstNumber && column === 0) {
			group = 1;
			groupIndex = row;
		} else if (row < firstNumber && column < blockWidth) {
			group = 2;
			groupIndex = row * firstNumber + column - 1;
		} else if (column < blockWidth) {
			group = 3;
			groupIndex = (row - firstNumber) * blockWidth + column;
		}

		const isMovingDot = column >= squareSide;
		const isCenter = row === center && column === center;
		const relocationClass = isMovingDot ? ' product-dot-relocating' : isCenter ? ' product-dot-center' : '';
		const style = {
			'--dot-delay': `${Math.min(groupIndex, 40) * 8}ms`,
			'--start-x': `${((column + .5) / rectangleColumns) * 100}%`,
			'--start-y': `${((row + .5) / squareSide) * 100}%`,
			'--dot-size': `${70 / rectangleColumns}%`,
		};
		if (isMovingDot) {
			const moveIndex = (column - squareSide) * rectangleRows + row;
			const targetRow = rectangleRows + Math.floor(moveIndex / squareSide);
			const targetColumn = moveIndex % squareSide;
			style['--end-x'] = `${((targetColumn + .5) / rectangleColumns) * 100}%`;
			style['--end-y'] = `${((targetRow + .5) / squareSide) * 100}%`;
			style['--move-delay'] = `${moveStart + moveIndex * moveStep}s`;
		} else if (isCenter) {
			style['--end-x'] = `${((squareSide - .5) / rectangleColumns) * 100}%`;
			style['--end-y'] = `${((squareSide - .5) / squareSide) * 100}%`;
			style['--center-move-delay'] = `${centerMoveDelay}s`;
		}

		return <span key={index} className={`product-dot product-dot-${group}${relocationClass}`} style={style} />;
	});

	return (
		<div className="product-animation">
			<div className="product-animation-toolbar">
				<label htmlFor="product-first-number">First number <strong>{firstNumber}</strong></label>
				<input id="product-first-number" type="range" min="1" max="4" value={firstNumber} onChange={event => {
					setFirstNumber(Number(event.target.value));
					setAnimationKey(key => key + 1);
				}} />
				<button type="button" onClick={() => setAnimationKey(key => key + 1)} aria-label="Replay dot animation">Replay</button>
			</div>
			<div className="product-equation" aria-live="polite">
				{firstNumber} × {firstNumber + 1} × {firstNumber + 2} × {firstNumber + 3} = <strong>{product.toLocaleString()}</strong>
			</div>
			<div className="product-drawing-frame">
				<div key={animationKey} className="product-dot-grid" style={{ width: `min(100%, ${rectangleColumns * 8}px)`, aspectRatio: `${rectangleColumns} / ${squareSide}`, '--square-shift': `${squareShift}%`, '--square-center-delay': `${squareCenterDelay}s` }} role="img" aria-label={`${product} dots begin in ${rectangleRows} rows and ${rectangleColumns} columns, then rearrange and center as a ${squareSide} by ${squareSide} square missing its center dot`}>
					{dots}
				</div>
			</div>
			<ol className="product-animation-key" aria-label="Animation stages">
				<li><i></i><span><b>{firstNumber}</b> dots</span></li>
				<li><i></i><span>finish the <b>{firstNumber} × {blockWidth}</b> block</span></li>
				<li><i></i><span>add <b>{firstNumber + 1}</b> complete copies</span></li>
				<li><i></i><span>complete the product</span></li>
			</ol>
			<div className="product-square-key"><span>build the rectangle</span><b>→</b><span>reshape dot by dot</span><b>→</b><span>move the center</span><b>→</b><strong>center {squareSide}²</strong></div>
		</div>
	);
};

const PythagoreanSquaresDiagram = () => {
	const divisions = [1, 2, 3, 4];

	return (
		<figure className="pythagorean-squares-diagram">
			<svg viewBox="35 -40 225 240" role="img" aria-labelledby="pythagorean-diagram-title pythagorean-diagram-desc">
				<title id="pythagorean-diagram-title">The 3–4–5 right triangle with a square on each side</title>
				<desc id="pythagorean-diagram-desc">The square on side 3 contains 9 unit squares, the square on side 4 contains 16, and the square on side 5 contains 25.</desc>

				<g className="pythagorean-square pythagorean-square-3">
					<path d="M110 50 H50 V110 H110 Z" />
					{[70, 90].map(value => <line key={`three-v-${value}`} x1={value} y1="50" x2={value} y2="110" />)}
					{[70, 90].map(value => <line key={`three-h-${value}`} x1="50" y1={value} x2="110" y2={value} />)}
				</g>

				<g className="pythagorean-square pythagorean-square-4">
					<path d="M110 110 H190 V190 H110 Z" />
					{[130, 150, 170].map(value => <line key={`four-v-${value}`} x1={value} y1="110" x2={value} y2="190" />)}
					{[130, 150, 170].map(value => <line key={`four-h-${value}`} x1="110" y1={value} x2="190" y2={value} />)}
				</g>

				<g className="pythagorean-square pythagorean-square-5">
					<path d="M110 50 L190 110 L250 30 L170 -30 Z" />
					{divisions.map(i => <line key={`five-parallel-${i}`} x1={110 + 12 * i} y1={50 - 16 * i} x2={190 + 12 * i} y2={110 - 16 * i} />)}
					{divisions.map(i => <line key={`five-cross-${i}`} x1={110 + 16 * i} y1={50 + 12 * i} x2={170 + 16 * i} y2={-30 + 12 * i} />)}
				</g>

				<path className="pythagorean-center-triangle" d="M110 50 V110 H190 Z" />
				<path className="pythagorean-center-corner" d="M110 96 H124 V110" />
				<g className="pythagorean-side-labels" aria-hidden="true">
					<text x="101" y="84">3</text><text x="150" y="125">4</text><text x="157" y="72">5</text>
				</g>
			</svg>
			<figcaption><span><i>a</i>²</span><i className="pythagorean-caption-operator">+</i><span><i>b</i>²</span><i className="pythagorean-caption-operator">=</i><strong><i>c</i>²</strong></figcaption>
		</figure>
	);
};

const CUBE_COLORS = ['#f2c14e', '#f78154', '#65d4cb', '#9184ee', '#62a8ea'];
const ISO_RISE_RATIO = .289;
const ISO_DEPTH_RATIO = .577;

const IsometricUnitCube = ({ x, y, size, color, flatness = 0 }) => {
	const half = size / 2;
	const rise = size * ISO_RISE_RATIO;
	const depth = size * ISO_DEPTH_RATIO;
	const mix = (from, to) => from + (to - from) * flatness;
	const points = values => values.map(point => point.join(',')).join(' ');
	const topFace = [
		[mix(x, x - half), mix(y, y - half)],
		[mix(x + half, x + half), mix(y + rise, y - half)],
		[mix(x, x + half), mix(y + rise * 2, y + half)],
		[mix(x - half, x - half), mix(y + rise, y + half)],
	];
	return (
		<g className="cube-sum-unit">
			<polygon className="cube-face cube-face-left" style={{ opacity: 1 - flatness }} fill={color} points={points([[x - half, y + rise], [x, y + rise * 2], [x, y + rise * 2 + depth * (1 - flatness)], [x - half, y + rise + depth * (1 - flatness)]])} />
			<polygon className="cube-face cube-face-right" style={{ opacity: 1 - flatness }} fill={color} points={points([[x, y + rise * 2], [x + half, y + rise], [x + half, y + rise + depth * (1 - flatness)], [x, y + rise * 2 + depth * (1 - flatness)]])} />
			<polygon className="cube-face cube-face-top" fill={color} points={points(topFace)} />
		</g>
	);
};

const SumOfCubesAnimation = ({ initialN = 4 }) => {
	const [n, setN] = useState(initialN);
	const [animationKey, setAnimationKey] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);
	const [elapsed, setElapsed] = useState(0);
	const elapsedRef = React.useRef(0);
	const lastFrameRef = React.useRef(null);
	const frameRef = React.useRef(null);
	const side = n * (n + 1) / 2;
	const animationBeats = side + Math.floor(n / 2);
	const cubePause = 1.25;
	const totalDuration = .45 + (animationBeats - 1) * .82 + (n - 1) * cubePause + .75 + .5;
	const targetWidth = Math.min(250, side * 25);
	const targetX = 674 - targetWidth;
	const targetY = 38;
	const targetUnit = targetWidth / side;
	const sourceStartX = n === 1 ? 210 : 62;
	const sourceStep = n === 1 ? 0 : 313 / (n - 1);
	const sourceLayout = Array.from({ length: n }, (_, index) => {
		if (n === 5) {
			const twoRowLayout = [
				{ x: 92, y: 95, labelY: 171 },
				{ x: 270, y: 95, labelY: 171 },
				{ x: 90, y: 226, labelY: 302 },
				{ x: 220, y: 226, labelY: 302 },
				{ x: 350, y: 226, labelY: 302 },
			];
			return twoRowLayout[index];
		}
		return { x: sourceStartX + index * sourceStep, y: 222, labelY: 302 };
	});
	const targetPoint = (column, row) => ({
		x: targetX + (column + .5) * targetUnit,
		y: targetY + (row + .5) * targetUnit,
	});

	useEffect(() => {
		if (!isPlaying) return undefined;
		const tick = time => {
			if (lastFrameRef.current === null) lastFrameRef.current = time;
			const next = Math.min(totalDuration, elapsedRef.current + (time - lastFrameRef.current) / 1000);
			lastFrameRef.current = time;
			elapsedRef.current = next;
			setElapsed(next);
			if (next < totalDuration) frameRef.current = window.requestAnimationFrame(tick);
			else setIsPlaying(false);
		};
		frameRef.current = window.requestAnimationFrame(tick);
		return () => {
			window.cancelAnimationFrame(frameRef.current);
			lastFrameRef.current = null;
		};
	}, [animationKey, isPlaying, totalDuration]);

	const replay = () => {
		elapsedRef.current = 0;
		lastFrameRef.current = null;
		setElapsed(0);
		setAnimationKey(key => key + 1);
		setIsPlaying(true);
	};
	const selectN = value => {
		setN(value);
		elapsedRef.current = 0;
		lastFrameRef.current = null;
		setElapsed(0);
		setAnimationKey(key => key + 1);
		setIsPlaying(true);
	};

	const placements = [];
	let sequence = 0;
	for (let k = n; k >= 1; k -= 1) {
		const offset = side - (k * (k + 1) / 2);
		const sourcePosition = sourceLayout[n - k];
		const sourceBaseX = sourcePosition.x;
		const sourceBaseY = sourcePosition.y;
		const sourceCell = Math.min(24, 88 / k);
		const layerDestinations = [];

		layerDestinations.push(Array.from({ length: k * k }, (_, index) => ({
			x: offset + index % k,
			y: offset + Math.floor(index / k),
		})));

		let topCursor = offset + k;
		let leftCursor = offset + k;
		let layer = 1;
		while (layer + 1 < k) {
			const topStart = topCursor;
			layerDestinations.push(Array.from({ length: k * k }, (_, index) => ({
				x: topStart + index % k,
				y: offset + Math.floor(index / k),
			})));
			topCursor += k;
			layer += 1;
			const leftStart = leftCursor;
			layerDestinations.push(Array.from({ length: k * k }, (_, index) => ({
				x: offset + index % k,
				y: leftStart + Math.floor(index / k),
			})));
			leftCursor += k;
			layer += 1;
		}
		if (layer < k) {
			const half = k / 2;
			layerDestinations.push(Array.from({ length: k * k }, (_, index) => {
				if (index < k * half) return {
					x: topCursor + index % half,
					y: offset + Math.floor(index / half),
				};
				const secondHalfIndex = index - k * half;
				return {
					x: offset + secondHalfIndex % k,
					y: leftCursor + Math.floor(secondHalfIndex / k),
				};
			}));
		}

		for (let movingLayer = 0; movingLayer < k; movingLayer += 1) {
			const z = k - 1 - movingLayer;
			const isSplitLayer = k % 2 === 0 && movingLayer === k - 1;
			for (let row = 0; row < k; row += 1) {
				for (let column = 0; column < k; column += 1) {
					const layerIndex = row * k + column;
					const destination = layerDestinations[movingLayer][layerIndex];
					const destinationPoint = targetPoint(destination.x, destination.y);
					const splitHalfOffset = isSplitLayer && layerIndex >= k * k / 2 ? 1 : 0;
					placements.push({
						key: `${k}-${z}-${row}-${column}`,
						k,
						z,
						row,
						column,
						destinationDepth: destination.x + destination.y,
						color: CUBE_COLORS[k - 1],
						sx: sourceBaseX + (column - row) * sourceCell / 2,
						sy: sourceBaseY + (column + row) * sourceCell * ISO_RISE_RATIO - z * sourceCell * ISO_DEPTH_RATIO,
						size: sourceCell - .6,
						dx: destinationPoint.x,
						dy: destinationPoint.y,
						delay: .45 + (sequence + splitHalfOffset) * .82 + (n - k) * cubePause,
					});
				}
			}
			sequence += isSplitLayer ? 2 : 1;
		}
	}

	const ease = value => value < .5 ? 4 * value ** 3 : 1 - ((-2 * value + 2) ** 3) / 2;
	const settled = placements.filter(item => elapsed >= item.delay + .75).length;
	const sourcePlacements = placements
		.filter(item => elapsed < item.delay)
		.sort((a, b) => b.k - a.k || (a.row + a.column) - (b.row + b.column) || a.z - b.z);
	const settledPlacements = placements
		.filter(item => elapsed >= item.delay + .75)
		.sort((a, b) => a.destinationDepth - b.destinationDepth || a.z - b.z);
	const movingPlacements = placements.filter(item => elapsed >= item.delay && elapsed < item.delay + .75);
	const completedCube = Array.from({ length: n }, (_, index) => n - index).find(k => {
		const thisCube = placements.filter(item => item.k === k);
		const nextCube = placements.filter(item => item.k === k - 1);
		const completedAt = Math.max(...thisCube.map(item => item.delay + .75));
		const nextStartsAt = nextCube.length ? Math.min(...nextCube.map(item => item.delay)) : totalDuration;
		return elapsed >= completedAt && elapsed < nextStartsAt;
	});
	const drawCube = item => {
		const raw = Math.max(0, Math.min(1, (elapsed - item.delay) / .75));
		const progress = ease(raw);
		const x = item.sx + (item.dx - item.sx) * progress;
		const y = item.sy + (item.dy - item.sy) * progress - Math.sin(progress * Math.PI) * 34;
		const size = item.size + (targetUnit - .7 - item.size) * progress;
		const flatness = Math.max(0, Math.min(1, (progress - .42) / .58));
		return <IsometricUnitCube key={item.key} x={x} y={y} size={size} color={item.color} flatness={flatness} />;
	};

	return (
		<div className="cube-sum-animation">
			<div className="cube-sum-toolbar">
				<label htmlFor="cube-sum-count">Largest cube <strong>{n}³</strong></label>
				<input id="cube-sum-count" type="range" min="1" max="5" value={n} onChange={event => selectN(Number(event.target.value))} />
				<div className="cube-sum-progress"><strong>{settled}</strong> of {side * side} placed{completedCube && <span> · {completedCube}³ complete</span>}</div>
				<button type="button" onClick={elapsed >= totalDuration ? replay : () => setIsPlaying(playing => !playing)}>{elapsed >= totalDuration ? 'Replay' : isPlaying ? 'Pause' : 'Continue'}</button>
			</div>
			<svg viewBox="0 0 720 340" role="img" aria-labelledby="cube-sum-title cube-sum-desc">
				<title id="cube-sum-title">Cubes rearranging to make a square</title>
				<desc id="cube-sum-desc">The cubes one cubed through {n} cubed are dismantled from the top. Each three-dimensional layer flattens in transit and tiles a two-dimensional {side} by {side} square.</desc>
				<rect className="cube-sum-target" x={targetX} y={targetY} width={side * targetUnit} height={side * targetUnit} rx="3" />
				{Array.from({ length: side + 1 }, (_, i) => <line key={`v-${i}`} className="cube-sum-gridline" x1={targetX + i * targetUnit} y1={targetY} x2={targetX + i * targetUnit} y2={targetY + side * targetUnit} />)}
				{Array.from({ length: side + 1 }, (_, i) => <line key={`h-${i}`} className="cube-sum-gridline" x1={targetX} y1={targetY + i * targetUnit} x2={targetX + side * targetUnit} y2={targetY + i * targetUnit} />)}
				<text className="cube-sum-label" x="42" y="38">CUBES</text>
				<text className="cube-sum-label" x="424" y="24">SQUARE OF THE SUM</text>
				{sourcePlacements.map(drawCube)}
				{settledPlacements.map(drawCube)}
				{movingPlacements.map(drawCube)}
				{Array.from({ length: n }, (_, index) => {
					const k = n - index;
					return <text key={k} className="cube-source-caption" x={sourceLayout[index].x} y={sourceLayout[index].labelY} textAnchor="middle">{k}³</text>;
				})}
			</svg>
			<div className="cube-sum-equation" aria-live="polite"><span>{Array.from({ length: n }, (_, i) => `${i + 1}³`).join(' + ')}</span><b>=</b><span>({Array.from({ length: n }, (_, i) => i + 1).join(' + ')})²</span><b>= {side * side}</b></div>
			<div className="cube-sum-key">{Array.from({ length: n }, (_, index) => {
				const k = n - index;
				return <span key={k}><i style={{ background: CUBE_COLORS[k - 1] }}></i>{k}³</span>;
			})}</div>
		</div>
	);
};

const DailyTeaser = ({ onOpenNote }) => {
	const today = new Date();
	const dateLabel = new Intl.DateTimeFormat('en-US', {
		year: 'numeric', month: 'long', day: 'numeric',
	}).format(today);
	const weekdayLabel = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(today);
	const monthYearLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(today);
	const monthOffset = new Date(today.getFullYear(), today.getMonth(), 1).getDay();
	const cyclePosition = today.getDate() + monthOffset;
	const cycleLength = Math.max(28, Math.ceil(cyclePosition / 7) * 7);
	const cycleWeekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
	const dayNumber = Math.floor(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) / 86400000);
	const requestedCuriosity = new URLSearchParams(window.location.search).get('curiosity');
	const curiosityOverrides = { cubes: 0, consecutive: 1, calendar: 2, sevenths: 3, pythagorean: 4 };
	const teaserIndex = curiosityOverrides[requestedCuriosity] ?? ((dayNumber % 5) + 5) % 5;
	const storageKey = `math-toys-curiosity-${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
	const [isOpen, setIsOpen] = useState(() => {
		try {
			return window.localStorage.getItem(storageKey) !== 'seen';
		} catch {
			return true;
		}
	});

	useEffect(() => {
		try {
			window.localStorage.setItem(storageKey, 'seen');
		} catch {
			// Storage may be unavailable in private or restricted browsing modes.
		}
	}, [storageKey]);

	const teasers = [
		{
			title: null,
			body: <>The sum of the cubes from 1 to <i>n</i> is the square of their sum. Watch the solid cubes unfold, layer by layer, into one perfect square.</>,
			preview: <SumOfCubesAnimation />,
			action: null,
		},
		{
			title: null,
			body: 'Pick any four consecutive integers and multiply them. Add 1, and the result is always a perfect square.',
			preview: <ConsecutiveProductAnimation />,
			action: <button type="button" onClick={() => onOpenNote('four-consecutive-integers')}>Weird, eh? See why <span aria-hidden="true">→</span></button>,
		},
		{
			title: null,
			body: <><span className="calendar-teaser-today">{weekdayLabel}, {dateLabel}.</span><span className="calendar-teaser-question">Why is this day a {weekdayLabel}?</span></>,
			preview: (
				<div className="calendar-teaser-demo">
					<div className="calendar-cycle" aria-label={`Weekly cycles numbered 1 through ${cycleLength}; day ${cyclePosition} is ${weekdayLabel}`}>
						<div className="calendar-cycle-head" aria-hidden="true">{cycleWeekdays.map(day => <span key={day} className={day === weekdayLabel ? 'calendar-cycle-weekday-today' : ''}>{day.slice(0, 3)}</span>)}</div>
						<div className="calendar-cycle-days">
							{Array.from({ length: cycleLength }, (_, index) => {
								const day = index + 1;
								return <span key={day} className={day === cyclePosition ? 'calendar-cycle-today' : ''} aria-label={`${day}: ${cycleWeekdays[index % 7]}`}>{day}</span>;
							})}
						</div>
					</div>
					<div className="calendar-position-explanation">
						<strong>{cyclePosition}? Why {cyclePosition}?</strong>
						<p>We get {cyclePosition} by adding today’s date to the offset for {monthYearLabel}, which is {monthOffset}. Look at the number of blanks in the first week:</p>
					</div>
					<div className="calendar-offset-example" aria-label={`${monthYearLabel}, showing ${monthOffset} offset spaces before the first day`}>
						<div className="calendar-offset-title">{monthYearLabel} <span>offset {monthOffset}</span></div>
						<div className="calendar-cycle-head" aria-hidden="true">{cycleWeekdays.map(day => <span key={day}>{day.slice(0, 3)}</span>)}</div>
						<div className="calendar-offset-days">
							{Array.from({ length: monthOffset }, (_, index) => <span className="calendar-offset-space" key={`offset-${index}`} aria-hidden="true">·</span>)}
							{Array.from({ length: 8 }, (_, index) => <span key={`date-${index + 1}`}>{index + 1}</span>)}
							<span className="calendar-offset-ellipsis" aria-hidden="true"><i></i><i></i><i></i></span>
						</div>
					</div>
					<div className="daily-teaser-action"><Link to="/calendar/instructions">Learn the calendar trick <span aria-hidden="true">→</span></Link></div>
				</div>
			),
			action: null,
		},
		{
			title: null,
			body: <>You’re familiar with 1/2 = 0.5 and 1/3 = 0.<span className="repeating-digits">3</span>. But if you really want to start getting adventurous with decimals, have a look at 1/7.</>,
			preview: (
				<div className="teaser-fraction teaser-sevenths">
					<span>1/7</span><strong>= 0.<span className="repeating-digits"><b>142</b><b>857</b></span></strong>
					<div className="teaser-sum">
						<small>And check this out:</small>
						<div className="stacked-sum" aria-label="142 plus 857 equals 999">
							<span>142</span>
							<span className="stacked-addend"><i>+</i>857</span>
							<strong>999</strong>
						</div>
					</div>
				</div>
			),
			action: <button type="button" onClick={() => onOpenNote('periodic-decimals-prime-reciprocals')}>Learn more <span aria-hidden="true">→</span></button>,
		},
		{
			title: null,
			body: null,
			preview: (
				<div className="pythagorean-teaser">
					<PythagoreanSquaresDiagram />
					<p className="pythagorean-teaser-intro">The familiar (3, 4, 5) triangle commonly illustrates the Pythagorean Theorem. But did you know there’s a Pythagorean triple for every odd number? For example, (5, 12, 13), (7, 24, 25), … (11, 60, 61), &amp;c. Let (<i>a</i>, <i>b</i>, <i>c</i>) represent a triple:</p>
					<div className="pythagorean-construction">
						<span>Pick an odd number <strong><i>a</i></strong></span>
						<span><strong><i>b</i></strong> = (<i>a</i>² − 1) / 2</span>
						<span><strong><i>c</i></strong> = (<i>a</i>² + 1) / 2</span>
					</div>
					<div className="daily-teaser-action"><Link to="/pythagorean-triples-lab">Explore Pythagorean triples <span aria-hidden="true">→</span></Link></div>
				</div>
			),
			action: null,
		},
	];
	const teaser = teasers[teaserIndex];

	return (
		<aside className={`daily-teaser ${isOpen ? 'daily-teaser-open' : 'daily-teaser-closed'}`} aria-labelledby="daily-teaser-label">
			<button
				type="button"
				className="daily-teaser-toggle"
				aria-expanded={isOpen}
				aria-controls="daily-teaser-content"
				onClick={() => setIsOpen(open => !open)}
			>
				<span id="daily-teaser-label">Today’s Curiosity</span>
				<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
			</button>
			{isOpen && (
				<div className="daily-teaser-content" id="daily-teaser-content">
					<div className="daily-teaser-copy">
						{teaser.title && <h2>{teaser.title}</h2>}
						{teaser.body && <p>{teaser.body}</p>}
						{teaser.action && <div className="daily-teaser-action">{teaser.action}</div>}
					</div>
					<div className="daily-teaser-preview">{teaser.preview}</div>
				</div>
			)}
		</aside>
	);
};

export default () => {
	// This is for adding the 'click' handler to each article title.
	useEffect(() => {
		var els = Array.from(document.querySelectorAll('.article-title'));
		els.forEach(el => {
			const article = el.closest('article');
			if (article && !article.id) article.id = articleSlug(el.querySelector('.article-title-text')?.textContent || 'article');
		});
		els.forEach(el => el.addEventListener('click', readArticle));

		const linkedArticle = window.location.hash
			? document.getElementById(decodeURIComponent(window.location.hash.slice(1)))
			: null;
		const linkedContent = linkedArticle?.querySelector('.article-closed, .article-opened');
		if (linkedContent) {
			linkedContent.classList.remove('article-closed');
			linkedContent.classList.add('article-opened');
			window.requestAnimationFrame(() => linkedArticle.querySelector('.article-title')?.scrollIntoView({ block: 'start' }));
		}
		return () => els.forEach(el => el.removeEventListener('click', readArticle));
	}, []);

	const openNote = id => {
		const article = document.getElementById(id);
		const content = article?.querySelector('.article-closed, .article-opened');
		const title = article?.querySelector('.article-title');
		if (!article || !content) return;
		content.classList.remove('article-closed');
		content.classList.add('article-opened');
		window.requestAnimationFrame(() => title?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
	};
  
	const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
	const yearTemplate = [0, 3, 3, 6, 1, 4, 6, 2, 5, 0, 3, 5];

		return (
    <HomeWrapper className="home-page">
      <section className="home-hero">
        <div className="hero-orbit" aria-hidden="true"><span>φ</span><span>π</span><span>√</span></div>
        <DailyTeaser onOpenNote={openNote} />
        <p className="home-eyebrow">Patterns · numbers · play</p>
        <h1>Ideas worth<br/><em>playing with.</em></h1>
        <p className="home-intro">Small experiments and curious observations from the endlessly surprising world of mathematics.</p>
        <div className="home-cue">Or choose a note to explore <span aria-hidden="true">↓</span></div>
      </section>
      <section className="home-notes" aria-label="Math notes">
      <article id="even-digit-palindromes">
        <ArticleTitle>Why Even-Digit Palindromes Are Divisible by 11</ArticleTitle>
        <ToggleRead className="article-closed">
          <p>A palindromic integer with an even number of digits is always divisible by 11 and so, with the exception of 11 itself, cannot be prime.</p>

          <p>The foundation is that any integer consisting of an even number of 0s, with a 1 on each end (1001, 100001, &amp;c) is divisible by 11. That is, 1 plus any odd power of 10 -- 10^(2n + 1) + 1 -- is 0 mod 11.</p>

          <p>A proof: 10 is -1 mod 11. Since powers of -1 alternate between -1 and 1, all odd powers of -1 are also -1. Therefore, 10 to any odd power is -1 mod 11. Since 10^(2n + 1) is -1 mod 11, 10^(2n + 1) + 1 is 0 mod 11.</p>

          <p>Let's look at what that means for our palindromic integer. Take as an example 137731. The outer "layer" is 100001, which is divisible by 11. Let's subtract that, leaving 37730. Next, divide by 10 to remove the 0, leaving 3773. Now, the outer "layer" is 3003. This is 3 x 1001, and 1001 is divisible by 11. Let's subtract 3003, leaving 770. Again, divide by 10, leaving 77, which is 7 x 11.</p>

          <p>
            137731 = 100001 + 30030 + 7700<br/>
            100001 = 11a, 37730 = 10x3x11b, 77 = 100x7x11<br/>
            Each element in this sum is a multiple of 11, so the number itself is a multiple of 11.
          </p>

          <p>The process illustrates thinking of the number as layers, with each layer being one digit, followed by an even number of zeros, followed by that same digit. When that number is divided by the digit, what's left is the pattern of 1, followed by an even number of 0s, followed by 1, which is always divisible by 11.</p>
        </ToggleRead>
      </article>

      <article className="reciprocal-article">
        <ArticleTitle>Reciprocal of 998001</ArticleTitle>

        <ToggleRead className="article-closed">
        <div className="reciprocal-intro">
          <p className="article-kicker">A decimal with one surprising omission</p>
          <div className="decimal-ribbon" aria-label="Zero point zero zero zero, zero zero one, zero zero two, zero zero three, continuing through nine hundred ninety-six, nine hundred ninety-seven, nine hundred ninety-nine">
            <span>0.</span><span>000</span><span>001</span><span>002</span><span>003</span><span className="decimal-ellipsis">…</span><span>996</span><span>997</span><span className="decimal-surprise">999</span>
          </div>
          <p>The decimal remains faultlessly predictable almost to 1000. Just before 999, however, the pattern apparently stumbles: <strong>998 is missing</strong>, and then the cycle begins again at 000, 001…</p>
        </div>

        <p>This is, of course, the decimal expansion of 1/998001. And because we're curious, we'd quite like to know what's going on. Why do we get most of the integers from 0 to 999, and why is 998 skipped?</p>

        <div className="article-insight">
          <span className="insight-label">The vital clue</span>
          <div className="equation-pair">
            <span>998001 = 999<sup>2</sup></span>
            <span>1 / 999 = 0.001 001 001…</span>
          </div>
          <p>The reciprocal of 10<sup>n</sup> − 1 is always an unending series of zero-padded 1s. Since 998001 is 999 &times; 999, we can examine 1/998001 by starting with 1/999 and dividing by 999.</p>
        </div>

        <h3>Follow the remainder</h3>
        <p>Let's set up our long division by placing 0.001001001… under the division bar and 999 to the left. We'll take the dividend in groups of three digits for convenience.</p>

        <figure className="division-work">
          <div className="division-scroll">
            <div className="division-layout" aria-hidden="true">
              <div className="division-quotient"><span>000</span><span>001</span><span>002</span></div>
              <div className="division-problem"><span className="division-divisor">999</span><span className="division-dividend">001 001 001 <i>…</i></span></div>
              <div className="division-working">
                <div className="division-subtract first"><span>−</span>000</div>
                <div className="division-rule first"></div>
                <div className="division-partial second"><b>1</b> 001</div>
                <div className="division-subtract second"><span>−</span> 999</div>
                <div className="division-rule second"></div>
                <div className="division-partial third"><b>2</b> 001</div>
                <div className="division-subtract third"><span>−</span>1 998</div>
                <div className="division-rule third"></div>
                <div className="division-partial fourth"><b>3</b> 001</div>
                <div className="division-more">⋮</div>
              </div>
            </div>
          </div>
          <table className="division-key">
            <thead>
              <tr><th>Step</th><th>Dividend</th><th>Quotient</th><th>Remainder</th></tr>
            </thead>
            <tbody>
              <tr><th scope="row">1</th><td>001</td><td>000</td><td>1</td></tr>
              <tr><th scope="row">2</th><td>1001</td><td>001</td><td>2</td></tr>
              <tr><th scope="row">3</th><td>2001</td><td>002</td><td>3</td></tr>
              <tr className="division-key-ellipsis"><td colSpan="4" aria-label="Steps omitted">…</td></tr>
              <tr><th scope="row">998</th><td>997001</td><td>997</td><td>998</td></tr>
              <tr className="division-key-error"><th scope="row">999</th><td>998001</td><td>998</td><td>999</td></tr>
            </tbody>
          </table>
        </figure>

        <p>Step 999 follows the pattern, but it produces a remainder that's equal to the divisor and so must be revised:</p>

        <table className="division-key division-key-corrected">
          <thead>
            <tr><th>Step</th><th>Dividend</th><th>Quotient</th><th>Remainder</th></tr>
          </thead>
          <tbody>
            <tr><th scope="row">999</th><td>998001</td><td>999</td><td>0</td></tr>
          </tbody>
        </table>

        <div className="article-conclusion-divider" aria-hidden="true"><span>◆</span></div>
        <p className="article-conclusion">This illustrates why 998 is missing from the decimal expansion.</p>
        </ToggleRead>
      </article>

      <article className="consecutive-article" id="four-consecutive-integers">
        <ArticleTitle>Four Consecutive Integers: One Less Than a Square</ArticleTitle>

        <ToggleRead className="article-closed">
          <div className="consecutive-intro">
            <p className="article-kicker">A hard-looking square root with a short way through</p>
            <div className="challenge-expression">
              <span className="radical">√</span><span className="radicand">48 × 49 × 50 × 51 + 1</span>
              <span className="challenge-equals">= ?</span>
            </div>
            <p>The challenge is to find this square root without multiplying four large numbers, adding 1, and then somehow extracting the root.</p>
          </div>

          <p>Now, of course you're not going to actually multiply those four numbers, add 1, and figure out the square root. No—the puzzle is to find a way to get the answer without doing all that work.</p>

          <p>At first, I wasn't seeing the trick. So I began looking for a pattern, starting with smaller sets of four consecutive integers.</p>

          <div className="pattern-cases" aria-label="The first three examples">
            <div><span>1 × 2 × 3 × 4 + 1</span><strong>25</strong><em>√25 = 5</em></div>
            <div><span>2 × 3 × 4 × 5 + 1</span><strong>121</strong><em>√121 = 11</em></div>
            <div><span>3 × 4 × 5 × 6 + 1</span><strong>361</strong><em>√361 = 19</em></div>
          </div>

          <p>Another pattern appears in the roots: each is 1 greater than the product of the first and last integers.</p>

          <div className="root-pattern">
            <span>1 × 4 + 1 = <strong>5</strong></span>
            <span>2 × 5 + 1 = <strong>11</strong></span>
            <span>3 × 6 + 1 = <strong>19</strong></span>
          </div>

          <div className="article-insight consecutive-answer">
            <span className="insight-label">Back to the challenge</span>
            <div className="answer-calculation"><span>48 × 51 + 1</span><strong>= 2449</strong></div>
            <p>So the square root of 48 × 49 × 50 × 51 + 1 is <strong>2449</strong>—with no four-number product required.</p>
          </div>

          <h3>Why it works</h3>
          <p>Let <i>x</i> be the first of the four integers. The product is <span className="inline-math">x(x + 1)(x + 2)(x + 3)</span>. Pair the outside terms and the inside terms:</p>

          <div className="proof-chain" aria-label="Algebraic proof">
            <div><span>Group the factors</span><strong>[x(x + 3)] [(x + 1)(x + 2)]</strong></div>
            <div><span>Expand each pair</span><strong>(x² + 3x)(x² + 3x + 2)</strong></div>
            <div><span>Let n be their midpoint</span><strong>n = x² + 3x + 1</strong></div>
            <div><span>The two factors are</span><strong>(n − 1)(n + 1)</strong></div>
            <div className="proof-result"><span>Difference of squares</span><strong>n² − 1</strong></div>
          </div>

          <p>The product is therefore <span className="inline-math">n² − 1</span>. Add 1 and we get <span className="inline-math">n²</span>, so its square root is simply <span className="inline-math">n = x² + 3x + 1 = x(x + 3) + 1</span>: one more than the first integer times the last.</p>

          <h3>A square with its center missing</h3>
          <div className="geometric-proof">
            <MissingCenterSquare />
            <div>
              <span className="insight-label">The smallest case</span>
              <strong>1 × 2 × 3 × 4 = 24 = 5² − 1</strong>
              <p>These 24 dots split into four 2-by-3 rectangles. Arranged around a center, they make an odd square with exactly its middle point missing.</p>
            </div>
          </div>

          <p>So the product of any four consecutive integers is one less than an odd perfect square. Larger cases admit the same missing-center interpretation; there's a related four-rectangle illustration on the Hex Clusters page.</p>
        </ToggleRead>
      </article>

      <article className="odd-square-article">
        <ArticleTitle>An Odd Square Minus 1 Is Always Divisible by 8</ArticleTitle>

        <ToggleRead className="article-closed">
          <div className="odd-square-intro">
            <p className="article-kicker">More than merely even</p>
            <div className="odd-square-example">
              <span>45² − 1</span><span>=</span><strong>2024</strong><span>=</span><strong>8 × 253</strong>
            </div>
            <p>2025 is the square of an odd number. Subtracting 1 plainly gives us an even number—but it turns out we can say considerably more.</p>
          </div>

          <p>Every odd integer can be written as <span className="inline-math">2x + 1</span> for some integer <i>x</i>. So let's square that expression and then subtract 1.</p>

          <div className="odd-proof" aria-label="Proof that an odd square minus one is divisible by eight">
            <div><span>Start with an odd number</span><strong>2x + 1</strong></div>
            <div><span>Square it</span><strong>(2x + 1)² = 4x² + 4x + 1</strong></div>
            <div><span>Subtract 1</span><strong>(2x + 1)² − 1 = 4x² + 4x</strong></div>
            <div><span>Factor</span><strong>4x² + 4x = 4x(x + 1)</strong></div>
          </div>

          <p>We have an obvious factor of 4. The remaining factor, <span className="inline-math">x(x + 1)</span>, is the product of two consecutive integers—and one of any two consecutive integers must be even.</p>

          <div className="parity-pair" aria-label="One of x and x plus one must be even">
            <span>x</span><span className="pair-link">consecutive</span><span>x + 1</span>
            <strong>one of these is always even</strong>
          </div>

          <p>Therefore <span className="inline-math">x(x + 1) = 2k</span> for some integer <i>k</i>. Substituting that into the factored expression finishes the argument:</p>

          <div className="odd-square-result">
            <span className="insight-label">The extra factor of 2</span>
            <strong>4x(x + 1) = 4(2k) = 8k</strong>
            <p>So the square of every odd integer is 1 more than a multiple of 8.</p>
          </div>
        </ToggleRead>
      </article>

      <article>
        <ArticleTitle>The 12-digit Calendar, Part 2</ArticleTitle>

        <ToggleRead className="article-closed">

        <p>OK, so you can get the day of the week for any date in 2021. That's neat. But what about for other years?</p>

        <p>So. Piscatology 101. Remember the 12-digit calendar for 2021? Well, the following table is a list of month offsets you will use to find dates in any whatever year you're looking for. You'll do this in three steps: 1) Find the base number for the year, 2) Find the number for your target month, 3) Find the day of the week for the target date.</p>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px'}}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)'}}>
            <div style={{gridArea: '1/1/2/7', textAlign: 'center', fontWeight: 'bold'}}>Month Offsets</div>
          { months.map((m, key) => <React.Fragment key={key}><div style={{paddingRight: '3px'}}>{m}</div><div>{yearTemplate[key]}</div></React.Fragment>) }
          </div>
        </div>

        <Footnote>So, you remember how you had to memorize your multiplication table in school, and you were eternally grateful that that was part of your education? ... Yeah, this is the same kind of thing.</Footnote>

	<p>Let's start with the most likely practical application: years in the recent past or future.</p>

        <ul className="steps">
          <li><b>First, get the base number for the year</b>.</li>
          <li>Start with the last two digits of the year you want.</li>
          <li>Divide that by 4, and add the integer result to your year<sup>*</sup>. (<i>If your year is a leap year, AND you're finding a date in January or February, subtract 1</i>.)</li>
          <li><b>Next, find your target month</b>.</li>
          <li>Get the month's offset value from the above table, and add it to the base number for the year. This is the target month's number. <i>Note: You can divide this by 7 and use the remainder, if that's more convenient for you</i>.</li>
          <li><b>Finally, get the day of the week</b>.</li>
          <li>Add the date to the target month's number.</li>
          <li>Divide by 7, and keep the remainder.</li>
          <li>The remainder is the number of the day of the week. (If there is no remainder, the date falls on a Saturday.)</li>
        </ul>

        <Footnote><sup>*</sup>Except for the step of dividing the year by 4, you can reduce any number you're working with by taking the remainder of that number divided by 7. For example, if you were working with the year 2085, you would divide 85 by 4, giving you 21. You could then reduce both 85 (1) and 21 (0) when you added them, giving you 1 as the base number for 2085. Note that this would be the same result as adding 85 and 21 and then taking the remainder of 106 divided by 7.</Footnote>

        <p>Let's try an example or two.</p>

        <p>How about September 16, 2025.</p>

	<ul>
          <li>Start with the last two digits of the year: 25.</li>
          <li>Divide by 4, and add the integer result to your year. The integer part of 25 divided by 4 is 6. Add 6 to 25, for a total of 31 (3, if you reduce).</li>
          <li>The offset for September is 5, so add 5 to 31, giving you 36 (1, if you reduce).</li>
          <li>The date is the 16th, so add 16 to 36 (or 1), giving you 52 (or 17).</li>
          <li>Divide 52 (or 17) by 7, and keep the remainder. The remainder is 3.</li>
          <li><i>September 16, 2025 falls on the 3rd day of the week, which is <b>Tuesday</b></i>.</li>
        </ul>

        <p>Now, let's try a leap year. How about February 4, 2028:</p>

	<ul>
          <li>Start with the last two digits of the year: 28.</li>
          <li>Divide by 4, and add the integer result to your year. The integer part of 28 divided by 4 is 7. Add 7 to 28, for a total of 35. <i>Since 2028 is a leap year, AND you're looking for a date in January or February, subtract 1, leaving 34 (6, if you reduce)</i>.</li>
          <li>The offset for February is 3, so add 3 to 34 (or 6), giving you 37 (or 9, which you could further reduce to 2).</li>
          <li>The date is the 4th, so add 4 to 37 (or 2), giving you 41 (or 6).</li>
          <li>Divide 41 (or 6) by 7, and keep the remainder. The remainder is 6.</li>
          <li><i>February 4, 2028 falls on the 6th day of the week, which is <b>Friday</b></i>.</li>
        </ul>

        <p style={{textAlign: 'center', fontWeight: 'bold'}}>0 3 3 6 1 4 6 2 5 0 3 5</p>

        </ToggleRead>
      </article>

      <article>
        <ArticleTitle>The 12-digit Calendar, Part 1</ArticleTitle>

        <ToggleRead className="article-closed">
        <p>2021: 5 1 1 4 6 2 4 0 3 5 1 3</p>

        <p>The concept is simple enough: each month is represented by a single digit in the range 0-6. If you've ever noticed how most months on a conventional calendar have a number of blank weekdays leading up to the first day of the month, that number of blanks is the digit for that month. For 2021, the digits are Jan: 5, Feb: 1, Mar: 1, Apr: 4, May: 6, Jun: 2, Jul: 4, Aug: 0, Sep: 3, Oct: 5, Nov: 1, Dec: 3.</p>

        <p>To calculate the day of the week a date lands on, you start by adding the date to the month's digit. Then, you divide by 7 and take the remainder. If there is no remainder, the day of the week for that date is Saturday. Otherwise, it's the number corresponding to the day within the week: 1 = Sunday, 2 = Monday, &c.</p>

        <p>Take June 28, 2021 as an example. The digit for June 2021 is 2. Adding this digit to the date (28) gives you 30. When you divide 30 by 7, you get 4 remainder 2. The 2nd day of the week is Monday. So June 28, 2021 is a Monday.</p>

        <p>A brother of one of my dad's uncles introduced me to this stuff ages ago. He showed me what the calendar looked like for 1975 and taught me how to use it, and I took an interest and made it a project to write out the calendars for centuries back, thus becoming very familiar with the pattern. For a list of 12-digit calendars spanning several centuries, starting with 1600, have a look at the <a href="/calendar">Gregorian Calendar</a> page.</p>

        </ToggleRead>
      </article>

      <article>
        <ArticleTitle>New Insight On 1/11</ArticleTitle>

        <ToggleRead className="article-closed">
        <p>In base 10, the reciprocal of 11&mdash;1/11&mdash;has the decimal expansion <Periodic whole="0" repeating="09" />. This caught my attention long ago, since 1/9 is <Periodic whole="0" repeating="11" />. That is, the period of 1/11 is 1 &times; 9, and the period of 1/9 is 1 &times; 11. Curious; but Why?</p>

        <p>As it happens, the answer is quite simple. It boils down to the fact that x<sup>2</sup> / (x + 1) = (x - 1) remainder 1. In this application, x is the number base: in this case, 10.</p>

        <p>This question came to the fore when a friend showed me a chart of the digits of the sexagesimal numbering system. I speculated that 1/11 in base 60 would have an expansion of <Periodic whole="0" repeating="0[59]" />, but I didn't have a handy way of demonstrating that&mdash;and I didn't feel like mucking about with the actual calculation.</p>

        <p>However, the pieces sort of fell in place. In the long division process for calculating 1/11, you have to add two 0s, since 11 necessarily does not go into 10 (regardless of the number base). Since 100 is the base squared, and 11 is the base + 1, you're dividing (x + 1) into x<sup>2</sup>, the result of which is (x - 1), with a remainder of 1. (Basic algebra: (x+1)(x-1) = x<sup>2</sup>-1.) So for base 60, you're dividing 61 into 3600. This gives you 59, remainder 1; and 59 is the highest digit in the sexagesimal system.</p>
        </ToggleRead>
      </article>

      <article>
        <p className="article-date">August 16, 2020</p>
        <ArticleTitle>Pythagorean Triples - Primitives</ArticleTitle>

	<ToggleRead className="article-closed">

	    <p>I took some interest in Pythagorean triples&mdash;specifically, those that don't have a common factor greater than 1. Examples include the familiar (3, 4, 5), (5, 12, 13), (7, 24, 25). In each of these cases, the difference between b and c is 1.</p>

	    <p>Another triple is (6, 8, 10), since 36 + 64 = 100. However, this isn't a primitive, as a, b, and c are all divisible by 2.</p>

	    <p>My approach to generating a Pythagorean triple is to start with c - b and then calculate values for a^2. I envision b^2 positioned within c^2, so that the two share the lower right corner. Then, a^2 wraps around b^2, with the corner of a^2 being the square of (c - b).</p>

	    <p>Let n = (c - b). Then the area of a^2 is n^2 + 2(nb). Since this must be square, I rearrange the terms: n(n + 2b). If n is square, then (n + 2b) must also be square. If n isn't square, then (n + 2b) must be a square, multiplied by some number that, if multiplied by n would yield a square. (For example, if n is 12, (n + 2b) would have to be a square multiplied by 3&mdash;or 3 times some square.)</p>

	    <p>Since I'm interested in primitives, I want to find triples that don't have common factors. This is true for all triples where n = 1. What about for n = 2? Since 2 isn't a square, (n + 2b) must be a square multiplied by 2. With (c - b) of 2, a^2 will be even, and a will therefore be even. Therefore, b must be odd if we want a primitive triple. So with n = 2, we are looking for square products of 2(2+2b) such that b is an odd number. Algebra: 2(2+2b) = 4+4b = 4(1 + b). Since 4 is square, 1 + b must be square, and b must be one less than an even square. So: 3, 15, 35, 63, &c. Let's look at these.</p>

	    <p>Start with b = 3. Then c = 5, and we have our familiar (3, 4, 5) triple, just rearranged to (a = 4, b = 3, c = 5). With b = 15, c = 17, and a^2 = 4(1 +15) = 64, yielding a = 8, so (a = 8, b = 15, c = 17). With b = 35, the triple is (a = 12, b = 35, c = 37). And so on.</p>

	    <p>What about with different values of c - b? How about 3? Using n = 3, we get 3 x (3 + 2b). Since 3 isn't square, (3 + 2b) must be a square multiplied by 3. For (3 + 2b) to be a multiple of 3, 2b must be a multiple of 3, and so b itself must be a multiple of 3. This precludes a primitive triple.</p>

	    <p>In general, primitives are possible only with values of n that are either odd squares or doubled squares. This is because for n as an odd square (n + 2b) can be a square without n and b having any common factors. Likewise, with n being double a square, (n + 2b).</p>

	</ToggleRead>
      </article>
 
      <article>
        <ArticleTitle>Prime Powers of 2 and Mersenne Primes</ArticleTitle>
	<ToggleRead className="article-closed">

        <p>So a Mersenne prime is a prime number of the form 2^n - 1. All known perfect numbers are based on Mersenne primes. I was toying one night with why the power of 2 for a Mersenne prime must itself be prime. I'm sure an algebraic proof would show that 2^n - 1 is factorable (and therefore not prime) if n is composite. But it was a different approach that occurred to me as I was lying there.</p>

        <p>Think of 2^n - 1 in its binary representation. It's just a repdigit of n 1s, right? So if n is composite&mdash;say a x b&mdash;then 2^n - 1 can be described as a group of b 1s, which is itself obviously composite. To illustrate, suppose n is 15 with a = 3, b = 5. Then 2^15 - 1 = 111111111111111, or 3 groups of 5 1s. Any number whose digits form multiple groups of the same pattern of digits can be expressed as a multiple of that pattern of digits&mdash;and therefore not a prime number.</p>
        </ToggleRead>
      </article>
 
      <article>
        <ArticleTitle>Golden Ratio Fiddlings</ArticleTitle>
	<ToggleRead className="article-closed">

        <p>So I've not explored this much yet, but it's looking curious to me.</p>

        <p>Familiar: phi, the Golden Ratio, in its fractional form is (1 + sqrt(5)) / 2.</p>

        <p>The powers of phi have interested me in their fractional form, with 2 as the constant denominator (even when it's possible to reduce): (a + b x sqrt(5)) / 2, since each a and b is the sum of the previous two, so that each a is a Lucas number and each b is a Fibonacci number&mdash; so: (1 + 1√5) / 2, (3 + 1√5) / 2, (4 + 2√5) / 2, (7 + 3√5) / 2. </p>

        <p>The interesting thing is that as these powers increase, the ratio of a / b approaches <math><msqrt><mn>5</mn></msqrt></math>, 2.23606...:</p>
<Table variant="math" bordered>
  <thead>
  <tr className="success text-success">
    <th>phi<sup>n</sup></th>
    <th>Fraction Form</th>
    <th>a / b</th>
    <th>Approx to √5</th>
  </tr>
  </thead>
  <tbody>
  <tr>
    <td>phi<sup>4</sup></td>
    <td>(7 + 3√5) / 2</td>
    <td>7/3</td>
    <td>2.3333...</td>
  </tr>
  <tr>
    <td>phi<sup>7</sup></td>
    <td>(29 + 13√5) / 2</td>
    <td>29 / 13</td>
    <td>2.2307...</td>
  </tr>
  <tr>
    <td>phi<sup>19</sup></td>
    <td>(9349 + 4181√5) / 2</td>
    <td>9349 / 4181</td>
    <td>2.23606...</td>
  </tr>
  </tbody>
</Table>

        <p>Playing with other sequences in which each element is the sum of the previous two, I see that the ratios appear to approach some irrational (I assume) real number, but it's not evident to me that it's a number that can be expressed tidily. 2.23067977499... is irrational but what the decimal form approximates is precisely the square root of 5.</p>

        <p>So why should it be that the ratios of a/b in increasingly larger powers of phi approach a number that's not just another of the infinitely many undistinguished irrational numbers out there but is instead the square root of an integer? And I suspect I'm looking at a slipknot.</p>

        </ToggleRead>
      </article>

      <article>
        <ArticleTitle>Fibonacci Numbers as Sums of Products of Fibonacci Numbers</ArticleTitle>
	<ToggleRead className="article-closed">

        <p>Let (a, b) be two consecutive Fibonacci numbers, and let (c, d) be two consecutive Fibonacci numbers. (a, b) can overlap (c, d). The number ac + bd is a Fibonacci number. Examples: (a=3, b=5), (c=8, d=13). Then ac = 24, bd = 65, ac + bd = 89.</p>

        <p>I don't have a proof that this always works, though I'm pretty sure it does.</p>

        </ToggleRead>
      </article>

      <article>
        <ArticleTitle>Reciprocals of Composite Denominators</ArticleTitle>
	<ToggleRead className="article-closed">

        <p>I've long known that when a denominator is prime (other than 2, 5), the period length of the reciprocal is the smallest 9s reptend that the number divides. For 7, that's 6; for 31, it's 15; for 41, it's 5; and so on. I don't believe there can be a pattern: it just depends on which value 10^n - 1 that prime number happens to divide. Of course, this number will be either one less than the prime number (as with 7), or a factor of that (as with 31 and 41). If the number is even, the period is internally complementary&mdash;that is, the sum of its two halves is a 9s reptend. For 1/7, the period is 142857, and 142 + 857 = 999.</p>

        <p>Today's observation has to do with the length of the period for the reciprocal of a composite number whose factors, again, do not include 2 and 5. For these, the period length is also the minimum 9s reptend that the denominator will divide.</p>

        <p>Today, I realized that the length of a composite denominator's period is determined by the lengths of the periods of the prime factors of that denominator. For example, consider 287 as the denominator. I chose this because 287 is 7 x 41, and 1/7 has a period length of 6, while 1/41 has a period length of 5. It is therefore expected that 1/287 has a period length of 30, because 30 is 6x5. And so it has: 1/287 = .003484320557491289198606271777...</p>

        <p>I needed to be satisfied that there was a reason why this should work. Since the minimum 9s reptend that 7 divides is 999999 (7x142957), and the minimum one that 41 divides is 99999 (41x2439), what must be the minimum 9s reptend that 287 divides? It would be the shortest one that's divisible by both 7 and 41, so it must be the smallest 9s reptend whose length is divisible by both 6 and 5. The least common multiple of 6 and 5 is 30.</p>

        </ToggleRead>
      </article>

      <article id="prime-reciprocal-periods" className="periodic-prime-article">
        <ArticleTitle>Description of Periodic Decimals of Reciprocals of Prime Numbers</ArticleTitle>
	<ToggleRead className="article-closed">

        <div className="periodic-prime-intro">
          <p>Let <i>p</i> be the denominator, a prime number that is not a factor of the base. So in base 10, <i>p</i> is not 2 or 5.</p>
        </div>

        <p className="periodic-prime-label">Observations:</p>

        <ul className="periodic-observations">
          <li>The maximum period length is <span className="inline-math">(<i>p</i> − 1)</span>.</li>
          <li>The period length is the minimum <i>n</i>, such that <span className="inline-math">10<sup><i>n</i></sup> − 1</span> is a multiple of <i>p</i>.</li>
          <li>The period length is either <span className="inline-math">(<i>p</i> − 1)</span> or a factor of <span className="inline-math">(<i>p</i> − 1)</span>.</li>
          <li>If the period length is even, the period can be split into two parts, the sum of which is <span className="inline-math">10<sup><i>x</i></sup> − 1</span>, where <i>x</i> is one half the period length.</li>
        </ul>

        <p>I actually did find it a little tiresome to try to defend some of these; however, I think I succeeded&mdash;or at least was confident of navigating there.</p>

        <div className="periodic-classic-examples">
          <p>Two classic examples: <span className="inline-math">1/7</span>, whose period is <span className="periodic-digits">142857</span>; and <span className="inline-math">1/13</span>, with period <span className="periodic-digits">076923</span>.</p>

          <div className="periodic-example-grid">
            <p>For 7, the maximum period length is 6, and the period length actually is 6.</p>

            <p>For 13, the maximum period length would be 12; however, the actual period length is 6, a factor of 12.</p>
          </div>
        </div>

        <p>For both 7 and 13, <span className="inline-math">10<sup>6</sup> − 1</span>, or <span className="periodic-digits">999999</span>, is the minimum such number of which they're factors. <span className="periodic-digits">9, 99, 999, 9999, and 99999</span> all leave remainders when divided by either 7 or 13.</p>

        <div className="periodic-complements">
          <p>Since the period lengths are even (both 6), they can be split in half, and the sum of the halves is <span className="inline-math">10<sup><i>x</i></sup> − 1</span>, where <i>x</i> is half the period length, or 3 in this case. <span className="complement-equation">So, for 7: <strong>142 + 857 = 999</strong>.</span> <span className="complement-equation">For 13: <strong>076 + 923 = 999</strong>.</span></p>
        </div>

        <button className="article-cross-link" type="button" onClick={() => openNote('periodic-decimals-prime-reciprocals')}>See why the halves add to all 9s <span aria-hidden="true">→</span></button>

        <p>In many cases, the period length is odd, so you don't get complementary halves. The determining factor is the smallest 9s repdigit (<span className="inline-math">10<sup><i>n</i></sup> − 1</span>) the prime number happens to divide. For example, not only do 7 and 13 divide <span className="periodic-digits">999999</span>: 37 also divides it, as do 3 and 11. However, the period lengths of <span className="inline-math">1/3</span>, <span className="inline-math">1/11</span>, and <span className="inline-math">1/37</span> are 1, 2, and 3, since 3 divides <span className="inline-math">10<sup>1</sup> − 1</span>, 11 divides <span className="inline-math">10<sup>2</sup> − 1</span>, and 37 divides <span className="inline-math">10<sup>3</sup> − 1</span>.</p>

        </ToggleRead>
      </article>

      <article id="periodic-decimals-prime-reciprocals" className="periodic-prime-article sevenths-proof-article">
        <ArticleTitle>Why the Two Halves of 1/7 Add to 999</ArticleTitle>
        <ToggleRead className="article-closed">

        <div className="periodic-prime-intro sevenths-proof-intro">
          <p>The repeating decimal for <span className="inline-math">1/7</span> is <span className="periodic-digits">0.142857…</span>. Split its six-digit period in half, and <span className="inline-math">142 + 857 = 999</span>. This is not a coincidence.</p>
        </div>

        <p>Let <i>p</i> be a prime denominator other than 2 or 5. Suppose its period length is even, and call it <span className="inline-math">2<i>n</i></span>. This means that <span className="inline-math">10<sup>2<i>n</i></sup> − 1</span> is the smallest number of the form <span className="inline-math">10<sup><i>x</i></sup> − 1</span> that <i>p</i> divides. For 7, the period length is 6, so <i>n</i> is 3.</p>

        <div className="periodic-proof-chain" aria-label="Why the remainder halfway through an even decimal period is p minus one">
          <div><span>Factor a difference of squares</span><strong>10<sup>2<i>n</i></sup> − 1 = (10<sup><i>n</i></sup> − 1)(10<sup><i>n</i></sup> + 1)</strong></div>
          <div><span>The first factor is too early</span><strong><i>p</i> ∤ (10<sup><i>n</i></sup> − 1)</strong></div>
          <div><span>So the prime divides the other factor</span><strong><i>p</i> | (10<sup><i>n</i></sup> + 1)</strong></div>
          <div className="periodic-proof-result"><span>Halfway through the period</span><strong>10<sup><i>n</i></sup> ≡ −1 ≡ <i>p</i> − 1 &nbsp;(mod <i>p</i>)</strong></div>
        </div>

        <p>In long division, that congruence tells us the remainder after the first <i>n</i> digits. The expansion begins with remainder 1, representing <span className="inline-math">1/<i>p</i></span>. Halfway through the period, the remainder is <span className="inline-math"><i>p</i> − 1</span>, so the second half begins with the digits of <span className="inline-math">(<i>p</i> − 1)/<i>p</i></span>.</p>

        <div className="sevenths-remainder-example">
          <span className="insight-label">For sevenths, 10³ ≡ 6 (mod 7)</span>
          <div><span>1/7</span><strong>= 0.<b>142</b><b>857</b>…</strong></div>
          <div><span>6/7</span><strong>= 0.<b>857</b><b>142</b>…</strong></div>
          <div className="sevenths-fraction-sum"><span>And</span><strong>1/7 + 6/7 = 1</strong></div>
        </div>

        <p>The two fractions add to 1; in their nonterminating decimal forms, their corresponding digits therefore add to repeating 9s. The first <i>n</i> digits of <span className="inline-math">(<i>p</i> − 1)/<i>p</i></span> are also the second half of the period of <span className="inline-math">1/<i>p</i></span>. That is why the two halves add to <span className="inline-math">10<sup><i>n</i></sup> − 1</span>.</p>

        </ToggleRead>
      </article>

      </section>
    </HomeWrapper>
	)
}
